import { Dialog, DialogActions, DialogContent, DialogProps, useTheme } from '@material-ui/core';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { DialogTitleWithFormStepper } from 'framework/dialogs/DialogTitleWithFormStepper';
import { FormDatePicker } from 'framework/forms/FormDatePicker';
import { FormSingleCheckboxField } from 'framework/forms/FormSingleCheckboxField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormActions } from 'framework/forms/PageableFormActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import {
	customerAccountLinesQuery_allForSettlementProposalWithOutstandingBalance,
	IPaymentFromSettlementProposalModel,
	paymentsForSalesCommand_newForSettlementProposal,
	settlementProposalsQuery_single,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import { FormSelectOrCreatePaymentMethodField } from '../../../../settings/paymentMethods/forms/FormSelectOrCreatePaymentMethodField';
import { CustomerAccountLinesTable } from './CustomerAccountLinesTable';
import { ICustomerAccountLineWithSelection } from './ICustomerAccountLineWithSelection';
import { TotalsTable } from './TotalsTable';
import { useNow } from 'shared/utils/useNow';

const createSchema = (strings: IStrings) => {
	return yup
		.object<IPaymentFromSettlementProposalModel>({
			date: yup.date().required(),
			isUseSettlementProposalReference: yup.boolean().defined(),
			methodId: yup.string().required(),
			reference: yup.string(),
			customerAccountLineIds: yup.mixed(),
		})
		.defined();
};

const EmptyValues: IPaymentFromSettlementProposalModel = {
	date: undefined as any,
	isUseSettlementProposalReference: true,
	methodId: '',
	reference: '',
	customerAccountLineIds: [],
};

const stepsRecord: Record<number, (keyof IPaymentFromSettlementProposalModel)[]> = {
	0: ['isUseSettlementProposalReference', 'reference', 'date'],
	1: ['methodId'],
	2: ['customerAccountLineIds'],
};

interface IProps extends DialogProps {
	settlementProposalId: string;
	customerAccountId: string;
	confirm: (id: string) => void;
	cancel: VoidFunction;
}

export const PaymentFromSettlementProposalForm = ({ confirm, cancel, settlementProposalId, customerAccountId, ...rest }: IProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [create, isSubmitting] = useFormSubmit(paymentsForSalesCommand_newForSettlementProposal);
	const [step, setStep] = useState<number>(0);
	const [settlementProposal] = useApiEffect(settlementProposalsQuery_single, settlementProposalId);
	const [allLines] = useApiEffect(customerAccountLinesQuery_allForSettlementProposalWithOutstandingBalance, customerAccountId, settlementProposalId);
	const [lines, setLines] = useState<ICustomerAccountLineWithSelection[]>([]);
	const now = useNow();

	useEffect(() => {
		if (allLines !== undefined) {
			setLines(allLines.map<ICustomerAccountLineWithSelection>(t => ({ isSelected: true, ...t })));
		}
	}, [allLines]);

	const handleSubmit = async (values: IPaymentFromSettlementProposalModel, helpers: FormikHelpers<IPaymentFromSettlementProposalModel>) => {
		const r = await create(settlementProposalId, { ...values, customerAccountLineIds: lines.filter(t => t.isSelected).map(t => t.id) });
		if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
			// navigate to detail?
			notify(strings.registeredWhat(strings.payment));
			confirm(r.result.objectId);
		}
	};

	if (lines === undefined || allLines === undefined || settlementProposal === undefined || now === undefined) {
		return <div></div>;
	}

	return (
		<Formik<IPaymentFromSettlementProposalModel>
			validateOnMount
			initialValues={{
				...EmptyValues,
				date: now,
			}}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<InnerDialog
					step={step}
					setStep={setStep}
					cancel={cancel}
					isSubmitting={isSubmitting}
					customerAccountIdentifier={settlementProposal.customerAccountIdentifier}
					lines={lines}
					setLines={setLines}
					settlementProposalReference={settlementProposal.reference ?? ''}
					{...rest}
				/>
			</Form>
		</Formik>
	);
};

interface IInnerDialogProps extends DialogProps {
	step: number;
	setStep: React.Dispatch<React.SetStateAction<number>>;
	cancel: VoidFunction;
	isSubmitting: boolean;
	customerAccountIdentifier: string;
	settlementProposalReference: string;
	lines: ICustomerAccountLineWithSelection[];
	setLines: React.Dispatch<React.SetStateAction<ICustomerAccountLineWithSelection[]>>;
}

const InnerDialog = ({
	step,
	setStep,
	cancel,
	isSubmitting,
	customerAccountIdentifier,
	lines,
	setLines,
	settlementProposalReference,
	...rest
}: IInnerDialogProps) => {
	const theme = useTheme();
	const props = useFormikContext<IPaymentFromSettlementProposalModel>();
	const strings = useLocalization();

	useEffect(() => {
		if (props.values.isUseSettlementProposalReference) {
			setFormValue<IPaymentFromSettlementProposalModel>('reference', settlementProposalReference, props);
		}
		// eslint-disable-next-line
	}, [props.values.isUseSettlementProposalReference]);

	return (
		<Dialog
			scroll='paper'
			{...rest}>
			<DialogTitleWithFormStepper
				title={`${strings.paymentOfSettlementProposal}: ${settlementProposalReference}`}
				step={step}
				labels={[`${strings.date} & ${strings.reference}`, strings.method, strings.lines]}
			/>
			<DialogContent
				className='df-col'
				dividers
				style={{ padding: step === 2 ? '0px' : '8px 24px' }}>
				{step === 0 && (
					<>
						<FormDatePicker<IPaymentFromSettlementProposalModel>
							name='date'
							label={strings.referenceDate}
							disableFuture
							required
						/>
						<FormSingleCheckboxField<IPaymentFromSettlementProposalModel>
							name='isUseSettlementProposalReference'
							label={strings.useReferenceOfSettlementProposalQuestion}
						/>
						<FormTextField<IPaymentFromSettlementProposalModel>
							name='reference'
							label={strings.paymentReference}
							disabled={props.values.isUseSettlementProposalReference}
						/>
					</>
				)}
				{step === 1 && (
					<>
						<FormSelectOrCreatePaymentMethodField<IPaymentFromSettlementProposalModel>
							name='methodId'
							label={strings.paymentMethod}
							mode='sale'
						/>
					</>
				)}
				{step === 2 && (
					<CustomerAccountLinesTable
						lines={lines}
						setLines={setLines}
					/>
				)}
			</DialogContent>
			<DialogActions>
				<div className='df-col'>
					{step === 2 && (
						<TotalsTable
							lines={lines}
							style={{ marginBottom: theme.spacing(1) }}
						/>
					)}
					<PageableFormActions
						step={step}
						setStep={setStep}
						cancel={cancel}
						isSubmitting={isSubmitting}
						submitText={strings.create}
						schema={createSchema(strings)}
						stepsRecord={stepsRecord}
					/>
				</div>
			</DialogActions>
		</Dialog>
	);
};
